import { FC } from 'react'
import { AdLibraryAd } from 'src/reactQuery/useGetAdsLibrarySearchQuery'
import { Skeleton } from 'src/components/Skeleton'
import { AdCard, AdCardData } from './AdCard'
import classes from './activeAds.module.scss'

// From adCard.module.scss: .card is radius 18. 400px matches the height the
// previous local placeholder reserved, which is the loaded card's footprint.
const AD_CARD_HEIGHT = 400
const AD_CARD_RADIUS = 18
const AD_SKELETON_COUNT = 3

interface ActiveAdsProps {
  ads?: AdLibraryAd[]
  isLoading?: boolean
  isPolitical?: boolean
  pageId?: string
  total: number
}

const mapAdToCardData = (ad: AdLibraryAd): AdCardData => ({
  assets: {
    links: ad.ad_creative_link_captions ?? [],
    // Meta exposes no creative media URLs, so there are no real photos to list.
    photos: [],
    texts: [
      ...(ad.ad_creative_bodies ?? []),
      ...(ad.ad_creative_link_titles ?? []),
    ],
  },
  body: ad.ad_creative_bodies?.[0],
  id: ad.id,
  impressions: ad.impressions,
  libraryId: ad.id,
  platforms: ad.publisher_platforms || ['facebook'],
  snapshotUrl: ad.ad_snapshot_url,
  startedRunning: ad.ad_delivery_start_time
    ? new Date(ad.ad_delivery_start_time).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '',
  status: ad.ad_active_status === 'ACTIVE' ? 'active' : 'inactive',
  targetGeography: (ad.target_locations || [])
    .filter((loc) => loc.type === 'countries' && !loc.excluded && loc.name)
    .map((loc) => ({ code: loc.code, name: loc.name as string })),
})

export const ActiveAds: FC<ActiveAdsProps> = ({
  ads,
  isLoading,
  isPolitical,
  pageId = '',
  total,
}) => {
  const pageLogo = `https://graph.facebook.com/${pageId}/picture?type=square`

  const displayAds = ads ?? []

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <h2 className={classes.title}>Active Ads</h2>
        <span className={classes.badge}>~{total} results</span>
      </div>
      {isLoading ? (
        <div className={classes.grid}>
          {Array.from({ length: AD_SKELETON_COUNT }).map((_, index) => (
            <Skeleton
              height={AD_CARD_HEIGHT}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              radius={AD_CARD_RADIUS}
            />
          ))}
        </div>
      ) : displayAds.length === 0 ? (
        <div className={classes.empty}>No active ads found</div>
      ) : (
        <div className={classes.grid}>
          {displayAds.map((ad) => (
            <AdCard
              data={mapAdToCardData(ad)}
              isPolitical={isPolitical}
              key={ad.id}
              pageId={pageId}
              pageLogo={pageLogo}
              pageName={ad.page_name || pageId}
            />
          ))}
        </div>
      )}
    </div>
  )
}
